import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';
import AddPetForm from '../components/AddPetForm';
import { useSubscriptionTier } from '../contexts/SubscriptionTierContext';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';
import { usePets } from '../hooks';
import type { Pet } from '../types';
import { BASIC_VAULT_MAX_PETS } from '../utils/constants';
import { formatDate } from '../utils/helpers';

// ─── Species config ─────────────────────────────────────────────────────────

const SPECIES_EMOJI: Record<string, string> = {
  dog: '🐶', cat: '🐱', bird: '🐦', rabbit: '🐰', other: '🐾',
};
const SPECIES_COLOR: Record<string, string> = {
  dog:    'bg-amber-500/20 text-amber-300',
  cat:    'bg-purple-500/20 text-purple-300',
  bird:   'bg-sky-500/20 text-sky-300',
  rabbit: 'bg-pink-500/20 text-pink-300',
  other:  'bg-brand-500/20 text-brand-300',
};

// ─── Pet Card ────────────────────────────────────────────────────────────────

function PetCard({ pet, onClick }: { pet: Pet; onClick: () => void }) {
  const age = pet.date_of_birth
    ? Math.floor((Date.now() - new Date(pet.date_of_birth).getTime()) / (1000 * 60 * 60 * 24 * 365))
    : null;

  return (
    <article
      id={`pet-card-${pet.id}`}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      className="glass-card p-4 flex items-center gap-4 hover:bg-white/10 transition-all duration-200 cursor-pointer group animate-slide-up focus:outline-none focus:ring-2 focus:ring-brand-400"
    >
      {/* Avatar */}
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0 ${SPECIES_COLOR[pet.species] ?? SPECIES_COLOR['other']}`}>
        {SPECIES_EMOJI[pet.species] ?? '🐾'}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <h2 className="font-display font-bold text-white text-lg leading-tight truncate group-hover:text-brand-300 transition-colors">
          {pet.name}
        </h2>
        <p className="text-white/50 text-sm truncate">{pet.breed}</p>
        <div className="flex items-center gap-3 mt-1">
          {age !== null && (
            <span className="text-xs text-white/40">{age} yr{age !== 1 ? 's' : ''}</span>
          )}
          {pet.weight && (
            <span className="text-xs text-white/40">{pet.weight} kg</span>
          )}
          {pet.date_of_birth && (
            <span className="text-xs text-white/30 hidden sm:block">
              Born {formatDate(new Date(pet.date_of_birth))}
            </span>
          )}
        </div>
      </div>

      {/* Chevron */}
      <span className="text-white/20 group-hover:text-brand-400 transition-colors text-lg">›</span>
    </article>
  );
}

// ─── Empty State ─────────────────────────────────────────────────────────────

function EmptyState({ onAdd, ready }: { onAdd: () => void; ready: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 animate-fade-in">
      {/* Illustration */}
      <div className="relative mb-6">
        <div className="w-28 h-28 rounded-full bg-brand-500/10 border-2 border-dashed border-brand-500/30 flex items-center justify-center">
          <span className="text-5xl" role="img" aria-label="paw">🐾</span>
        </div>
        <div className="absolute -bottom-1 -right-1 w-10 h-10 bg-accent-500 rounded-full flex items-center justify-center shadow-lg">
          <span className="text-white font-bold text-xl leading-none">+</span>
        </div>
      </div>

      <h2 className="font-display text-2xl font-bold text-white mb-2">
        No pets yet
      </h2>
      <p className="text-white/50 text-sm max-w-xs mb-8">
        Add your first furry (or feathery!) friend to start building their health passport.
      </p>

      <button
        id="empty-state-add-pet"
        onClick={onAdd}
        disabled={!ready}
        title={!ready ? 'Loading vault plan…' : undefined}
        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="text-lg">+</span> Add my first pet
      </button>
    </div>
  );
}

// ─── Loading Skeleton ────────────────────────────────────────────────────────

function PetCardSkeleton() {
  return (
    <div className="glass-card p-4 flex items-center gap-4 animate-pulse">
      <div className="w-14 h-14 rounded-2xl bg-white/10 flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-5 w-32 bg-white/10 rounded-lg" />
        <div className="h-3 w-24 bg-white/10 rounded-lg" />
      </div>
    </div>
  );
}

// ─── Dashboard Page ───────────────────────────────────────────────────────────

/**
 * DashboardPage — the main authenticated view.
 * Lists all pets belonging to the signed-in user.
 */
function DashboardPage() {
  const navigate = useNavigate();
  const { pets, status, error, addPet } = usePets();
  const { tier, ready } = useSubscriptionTier();
  const { showUpgrade } = useUpgradeModal();
  const [showForm, setShowForm] = useState(false);

  const petLimitReached =
    ready && tier === 'basic' && pets.length >= BASIC_VAULT_MAX_PETS;

  const openAddPet = () => {
    if (!ready) return;
    if (petLimitReached) {
      showUpgrade('pets');
      return;
    }
    setShowForm(true);
  };

  const handleAddPet = async (payload: Parameters<typeof addPet>[0]) => {
    await addPet(payload);
    setShowForm(false);
  };

  return (
    <>
      <NavBar />

      <main className="page-container pt-20">
        {/* Page header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-2xl font-bold text-white">My Pets</h1>
            {status === 'success' && pets.length > 0 && (
              <p className="text-white/40 text-sm mt-0.5">
                {pets.length} pet{pets.length !== 1 ? 's' : ''} registered
              </p>
            )}
          </div>

          {/* Show the FAB only if there are already some pets */}
          {pets.length > 0 && (
            <button
              id="dashboard-add-pet"
              type="button"
              disabled={!ready}
              onClick={openAddPet}
              title={
                !ready
                  ? 'Loading vault plan…'
                  : petLimitReached
                    ? 'Basic Vault — upgrade for more pets'
                    : undefined
              }
              className={`btn-primary py-2 px-4 text-sm ${petLimitReached && ready ? 'opacity-80' : ''}`}
            >
              <span className="text-base">+</span> Add pet
            </button>
          )}
        </div>

        {/* Loading skeletons */}
        {status === 'loading' && (
          <div className="space-y-3">
            {[1, 2, 3].map((n) => <PetCardSkeleton key={n} />)}
          </div>
        )}

        {/* Error state */}
        {status === 'error' && error && (
          <div role="alert" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-2xl px-5 py-4">
            <p className="font-semibold mb-1">Failed to load pets</p>
            <p className="text-red-400/70">{error}</p>
          </div>
        )}

        {/* Empty state */}
        {status === 'success' && pets.length === 0 && (
          <EmptyState onAdd={openAddPet} ready={ready} />
        )}

        {/* Pet list */}
        {status === 'success' && pets.length > 0 && (
          <div className="space-y-3">
            {pets.map((pet) => (
              <PetCard
                key={pet.id}
                pet={pet}
                onClick={() => navigate(`/pets/${pet.id}`)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Add Pet modal */}
      {showForm && (
        <AddPetForm
          onSubmit={(payload) => handleAddPet(payload)}
          onClose={() => setShowForm(false)}
        />
      )}
    </>
  );
}

export default DashboardPage;
